import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ClockSolidIcon16pxSvg from "../assets/images/icons/ClockSolidIcon16pxSvg";
import { StyledTextFieldTimePicker } from "./StyledComponents";

const CustomTimePickerHaveSecondV2 = (props) => {
  const { t } = useTranslation();

  const {
    value,
    onTimeChange,
    hoursLabel,
    name,
    maxHours = 23,
    className,
    readOnly = false,
  } = props;

  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [inputValue, setInputValue] = useState("00:00:00");

  /* ===============================
   * Utils
   ================================= */
  const isValidTime = (val) => {
    const match = val.match(/^(\d{2}):(\d{2}):(\d{2})$/);
    if (!match) return false;

    const h = Number(match[1]);
    const m = Number(match[2]);
    const s = Number(match[3]);

    return h <= maxHours && m <= 59 && s <= 59;
  };

  const formatTime = (h, m, s) =>
    `${String(h).padStart(2, "0")}:${String(m).padStart(
      2,
      "0"
    )}:${String(s).padStart(2, "0")}`;

  /* ===============================
   * Sync from outside value
   ================================= */
  useEffect(() => {
    const val = value || "00:00:00";
    setInputValue(val);

    const [h = 0, m = 0, s = 0] = val.split(":").map(Number);
    setHours(h);
    setMinutes(m);
    setSeconds(s);
  }, [value]);

  /* ===============================
   * Emit value
   ================================= */
  const emitTime = (h, m, s) => {
    const formatted = formatTime(h, m, s);
    setInputValue(formatted);
    onTimeChange?.(formatted);
  };

  /* ===============================
   * Handle input outside (HH:mm:ss)
   ================================= */
  const handleInputChange = (e) => {
    let val = e.target.value.replace(/[^\d:]/g, "");
    if (val.length > 8) return;

    setInputValue(val);

    if (!isValidTime(val)) return;

    const [h, m, s] = val.split(":").map(Number);
    setHours(h);
    setMinutes(m);
    setSeconds(s);
    onTimeChange?.(val);
  };

  const handleInputBlur = () => {
    if (!isValidTime(inputValue)) {
      emitTime(hours, minutes, seconds);
    }
  };

  /* ===============================
   * Dialog handlers
   ================================= */
  const handleHoursChange = (e) => {
    let h = Number(e.target.value);
    if (h > maxHours) h = 0;
    setHours(h);
    emitTime(h, minutes, seconds);
  };

  const handleMinutesChange = (e) => {
    let m = Number(e.target.value);
    if (m > 59) m = 0;
    setMinutes(m);
    emitTime(hours, m, seconds);
  };

  const handleSecondsChange = (e) => {
    let s = Number(e.target.value);
    if (s > 59) s = 0;
    setSeconds(s);
    emitTime(hours, minutes, s);
  };

  /* ===============================
   * Render
   ================================= */
  return (
    <div>
      {/* ===== Input outside ===== */}
      <StyledTextFieldTimePicker
        name={name}
        className={className}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        placeholder="HH:mm:ss"
        variant="outlined"
        size="small"
        inputProps={{
          maxLength: 8,
          inputMode: "numeric",
          pattern: "[0-9]{2}:[0-9]{2}:[0-9]{2}",
        }}
        InputProps={{
          readOnly: readOnly,
          sx: {
            height: "40px",
            backgroundColor: readOnly ? "#F3F4F6" : "#FFFFFF",
            cursor: readOnly ? "not-allowed" : "text",

            "& .MuiInputBase-input": {
              color: readOnly ? "#9CA3AF" : "#171717",
              WebkitTextFillColor: readOnly ? "#9CA3AF" : "#171717", // fix chrome autofill
            },
          },
          endAdornment: (
            <InputAdornment position="end">
              <div className="flex items-center">
                {hoursLabel && (
                  <div className="px-3 py-3 border-l">
                    <p className="text-[#6B7280]">{t("common.hours")}</p>
                  </div>
                )}
                <IconButton
                  onClick={() => {
                    if (readOnly) return;
                    setOpen(true);
                  }}
                  disableRipple
                  sx={{
                    backgroundColor: "#FFFFFF !important",
                    borderRadius: "8px",
                    p: 0.5,
                    "&:hover": { backgroundColor: "#F9FAFB" },
                    height: "40px",
                  }}
                >
                  <ClockSolidIcon16pxSvg />
                </IconButton>
              </div>
            </InputAdornment>
          ),
        }}
      />

      {/* ===== Dialog ===== */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{t("common.select-time")}</DialogTitle>
        <DialogContent>
          <div className="flex space-x-2 items-center pt-2">
            <TextField
              className="w-28"
              label={t("common.hours")}
              type="number"
              value={String(hours).padStart(2, "0")}
              onChange={handleHoursChange}
            />

            <p>:</p>

            <TextField
              className="w-28"
              label={t("common.minutes")}
              type="number"
              value={String(minutes).padStart(2, "0")}
              onChange={handleMinutesChange}
            />

            <p>:</p>

            <TextField
              className="w-28"
              label={t("common.seconds")}
              type="number"
              value={String(seconds).padStart(2, "0")}
              onChange={handleSecondsChange}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomTimePickerHaveSecondV2;
